import appConstants from '@/plugins/constants';
import { getSystemCustomFields } from './taskListConstants';
import { displayLocationLabel } from '../../../../utils/locationName';
import {
  ASSIGNMENT_LOCATION_TYPE,
  LEGACY_TASK_TYPE_LABEL_CHECKLIST,
  LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME,
  SIGN_OFF,
} from '../../../../utils/constants';
import { assignmentHasMixedLocationTypes } from '../../../../utils/locationMasterType';
import { normalizeUserIdList } from './assignmentRoles';
import { resolveEvidenceFieldState } from '../../../Project/TaskList/drawers/AddTaskDialog/addTaskDialogUtils';

const TASK_TYPES = appConstants.TASK_LIST_CONSTANTS.TASK_TYPES;

/**
 * Map a raw cycle_type to a CycleTypeTabs bucket (FBC / RM / IA / OTHERS).
 * Unknown values roll into OTHERS so counts match the OTHERS table filter.
 */
export function resolveCycleBucket(raw) {
  const key = String(raw || 'FBC').trim().toUpperCase() || 'FBC';
  if (key === 'FBC' || key === 'RM' || key === 'IA') return key;
  return 'OTHERS';
}

/** Empty per-cycle count map for CycleTypeTabs. */
export function emptyCycleCounts() {
  return { FBC: 0, RM: 0, IA: 0, OTHERS: 0 };
}

/**
 * Count items by cycle bucket using cycle_type / cycleType fields.
 * @param {Array} items
 * @returns {{ FBC: number, RM: number, IA: number, OTHERS: number }}
 */
export function buildCycleCounts(items = []) {
  const counts = emptyCycleCounts();
  (items || []).forEach((item) => {
    const key = resolveCycleBucket(item?.cycle_type || item?.cycleType);
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

export function getTaskTypeConstants(vm) {
  return vm?.$constants?.TASK_LIST_CONSTANTS?.TASK_TYPES || TASK_TYPES;
}

/**
 * User-facing task type label. Legacy stored "Normal Task" still displays as "Checklist".
 * @param {string|null|undefined} taskType
 * @returns {string}
 */
export function getTaskTypeDisplayLabel(taskType) {
  if (taskType == null || taskType === '') return '—';
  if (
    taskType === LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME
    || taskType === LEGACY_TASK_TYPE_LABEL_CHECKLIST
  ) {
    return LEGACY_TASK_TYPE_LABEL_CHECKLIST;
  }
  return taskType;
}

export function isAccountReconciliation(taskType, vm) {
  const types = getTaskTypeConstants(vm);
  return taskType === types.ACCOUNT_RECONCILIATION;
}

function formatCreatedOn(value) {
  if (!value) return '—';
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function resolveLocationNames(template = {}, locations = []) {
  // Prefer API enrichment when present (always has correct names for assigned locations).
  const details = Array.isArray(template.location_details) ? template.location_details : [];
  if (details.length) {
    const names = [];
    const seen = new Set();
    details.forEach((loc) => {
      const id = loc?._id != null ? String(loc._id) : loc?.id != null ? String(loc.id) : '';
      const name = loc?.name || loc?.description || '';
      if (!name || (id && seen.has(id))) return;
      if (id) seen.add(id);
      names.push(name);
    });
    if (names.length) return names;
  }

  const locationRows = Array.isArray(template.location) ? template.location : [];
  const ids = [];

  locationRows.forEach((row) => {
    // Prefer location_id — Mongo subdocs may also have their own _id.
    const raw = row?.location_id ?? row?.location ?? null;
    const id = raw != null && typeof raw === 'object' ? String(raw._id || raw.id || '') : raw != null ? String(raw) : '';
    if (id && !ids.includes(id)) ids.push(id);
  });

  if (!ids.length && Array.isArray(template.location_ids)) {
    template.location_ids.forEach((raw) => {
      const id = raw != null ? String(raw) : '';
      if (id && !ids.includes(id)) ids.push(id);
    });
  }

  const byId = new Map();
  (locations || []).forEach((loc) => {
    const id = loc?._id != null ? String(loc._id) : loc?.id != null ? String(loc.id) : '';
    if (!id) return;
    byId.set(id, loc.name || loc.location || loc.description || '');
  });

  return ids.map((id) => displayLocationLabel(byId.get(id) || id));
}

function buildOverflowDisplay(values = []) {
  const list = (values || []).filter(Boolean);
  if (!list.length) {
    return {
      primary: '—',
      overflowCount: 0,
      overflowItems: [],
      filterValue: '',
    };
  }
  return {
    primary: list[0],
    overflowCount: Math.max(0, list.length - 1),
    overflowItems: list.slice(1),
    filterValue: list.join(', '),
  };
}

export function getCustomFieldValue(template = {}, columnName) {
  if (!columnName) return '';
  if (template[columnName] != null && template[columnName] !== '') {
    return template[columnName];
  }
  if (Array.isArray(template.customfields)) {
    for (const entry of template.customfields) {
      if (!entry || typeof entry !== 'object') continue;
      if (Object.prototype.hasOwnProperty.call(entry, columnName)) {
        return entry[columnName];
      }
      if (entry.columnName === columnName && entry.value != null) {
        return entry.value;
      }
    }
  }
  return '';
}

export function formatTemplateRow(template = {}, locations = [], customColumns = []) {
  const tags = template.tags || template.frequency || [];
  const tagList = Array.isArray(tags)
    ? tags.map((t) => (typeof t === 'string' ? t : t?.name || t?.label || '')).filter(Boolean)
    : String(tags || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

  const locationNames = resolveLocationNames(template, locations);
  const locationOverflow = buildOverflowDisplay(locationNames);
  const frequencyDisplay = buildOverflowDisplay(tagList);

  const createdRaw =
    template.created_at ||
    template.createdAt ||
    template.createdon ||
    template.created_on ||
    template.created;

  const enabled = template.status !== false && !template.disabled;

  const customValues = {};
  (customColumns || []).forEach((col) => {
    if (!col?.columnName || col.isSystem) return;
    customValues[col.columnName] = getCustomFieldValue(template, col.columnName);
  });

  // Keep API-shaped fields intact for edit/hydrate. Use *Display fields for the table only.
  const id =
    template._id != null && typeof template._id === 'object'
      ? String(template._id._id || template._id.$oid || template._id.id || '')
      : template._id != null
        ? String(template._id)
        : '';

  return {
    ...template,
    _id: id || template._id,
    ref: template.ref || template.reference || (id ? id.slice(-6) : '—'),
    description: template.description || template.name || '—',
    task_type: template.task_type || '',
    taskTypeDisplay: getTaskTypeDisplayLabel(template.task_type),
    category: template.category || template.Category || '',
    subcategory: template.subcategory || template.sub_category || template.SubCategory || '',
    cycle_type: (template.cycle_type || template.cycleType || 'FBC').toUpperCase(),
    tags: Array.isArray(template.tags) ? template.tags : tagList,
    // Array of location names so ColumnFilter (and filterByDynamicCriteria) expose
    // each location as its own option, same as Setup Users. Empty → "(Blank)".
    locationDisplay: locationNames.slice(),
    locationPrimary: locationOverflow.primary,
    locationOverflowCount: locationOverflow.overflowCount,
    locationOverflowItems: locationOverflow.overflowItems,
    frequencyDisplay: frequencyDisplay.filterValue || '—',
    frequencyPrimary: frequencyDisplay.primary,
    frequencyOverflowCount: frequencyDisplay.overflowCount,
    frequencyOverflowItems: frequencyDisplay.overflowItems,
    duedays: template.duedays ?? template.due_days ?? '',
    createdOn: formatCreatedOn(createdRaw),
    statusDisplay: enabled ? 'Enabled' : 'Disabled',
    _enabled: enabled,
    ...customValues,
  };
}

export function formatCustomFieldRows(
  customColumns = [],
  cycleType = 'FBC',
  locationHeader = 'Location'
) {
  const custom = (customColumns || []).map((col) => ({
    ...col,
    displayName: col.displayName || col.displayText || col.columnName,
    dataType: col.dataType || '—',
    ingestionType: col.ingestionType || 'Custom',
    isSystem: false,
    cycleType: (col.cycleType || col.cycle_type || cycleType).toUpperCase(),
  }));

  return [...getSystemCustomFields(locationHeader), ...custom];
}

function isDueDaysBlank(value) {
  return value == null || value === '' || (typeof value === 'string' && !value.trim());
}

export function validateTemplateForm(
  form = {},
  assignments = [],
  vm,
  locations = [],
  locationHeader = 'Location'
) {
  const errors = {};
  const types = getTaskTypeConstants(vm);
  const locLabel = locationHeader || 'Location';

  if (!form.description?.trim()) errors.description = 'Template name is required';
  if (!form.task_type) errors.task_type = 'Task type is required';
  if (!form.cycle_type && !form.cycleType) errors.cycle_type = 'Cycle type is required';
  const due = Number(form.duedays);
  if (isDueDaysBlank(form.duedays)) {
    errors.duedays = 'Due days is required';
  } else if (!Number.isFinite(due)) {
    errors.duedays = 'Due days must be a valid number';
  } else if (due < -200 || due > 200) {
    errors.duedays = 'Due days must be between -200 and 200';
  }

  if (!assignments?.length) {
    errors.assignments = 'Add at least one assignment';
  } else {
    const locationNameById = new Map();
    (locations || []).forEach((loc) => {
      const id =
        loc?._id != null
          ? String(loc._id)
          : loc?.id != null
            ? String(loc.id)
            : '';
      if (!id) return;
      const name = loc.name || loc.description || loc.location || '';
      if (name) locationNameById.set(id, name);
    });

    const seenLocationIds = new Map();

    assignments.forEach((a, i) => {
      if (!a.location_ids?.length) {
        errors[`assignment_${i}`] = `Select at least one ${locLabel}`;
      } else if (assignmentHasMixedLocationTypes(a.location_ids, locations)) {
        errors[`assignment_${i}`] = ASSIGNMENT_LOCATION_TYPE.MIXED_ERROR;
      }
      if (!a.preparer?.length) errors[`assignment_${i}_preparer`] = 'Preparer is required';
      if (!a.approver?.length) errors[`assignment_${i}_approver`] = 'Approver is required';

      (a.location_ids || []).forEach((rawId) => {
        const id =
          rawId != null && typeof rawId === 'object'
            ? String(rawId._id || rawId.id || '')
            : rawId != null
              ? String(rawId)
              : '';
        if (!id) return;
        if (seenLocationIds.has(id)) {
          const name = locationNameById.get(id) || id;
          errors.duplicate_location = `"${name}" is already selected in another assignment`;
          return;
        }
        seenLocationIds.set(id, i);
      });

      const preparerIds = new Set(
        (a.preparer || [])
          .map((id) => String(typeof id === 'object' ? id?._id || id?.id : id))
          .filter(Boolean)
      );
      const hasOverlap = (a.approver || []).some((id) => {
        const approverId = String(typeof id === 'object' ? id?._id || id?.id : id);
        return approverId && preparerIds.has(approverId);
      });
      if (hasOverlap) {
        errors[`assignment_${i}_approver`] = 'Preparer & Approver cannot be same';
      }

      const requiresSignOff = !!a.requires_sign_off;
      const signOffArr = normalizeUserIdList(a.sign_off_approvers);
      if (requiresSignOff && !signOffArr.length) {
        errors[`assignment_${i}_sign_off_approvers`] = SIGN_OFF.ERRORS.REQUIRED_WHEN_ENABLED;
      }
      if (signOffArr.length && !a.approver?.length) {
        errors[`assignment_${i}_sign_off_approvers`] = SIGN_OFF.ERRORS.ORPHAN_L2;
      }
      if (signOffArr.length) {
        const preparerOverlap = signOffArr.filter((id) => preparerIds.has(id));
        if (preparerOverlap.length) {
          errors[`assignment_${i}_sign_off_approvers`] = SIGN_OFF.ERRORS.PREPARER_SOD;
        }
        const approverIdSet = new Set(
          (a.approver || [])
            .map((id) => String(typeof id === 'object' ? id?._id || id?.id : id))
            .filter(Boolean)
        );
        const approverOverlap = signOffArr.filter((id) => approverIdSet.has(id));
        if (approverOverlap.length) {
          errors[`assignment_${i}_sign_off_approvers`] = SIGN_OFF.ERRORS.APPROVER_SOD;
        }
      }
    });
  }

  if (!form.category) errors.category = 'Category is required';
  if (!form.subcategory) errors.subcategory = 'Sub category is required';

  if (form.task_type === types.ACCOUNT_RECONCILIATION) {
    if (!form.glcode?.length) errors.glcode = 'Select at least one account';
    (assignments || []).forEach((a, i) => {
      const glcodes = a?.glcodes || a?.glcode || [];
      if (!glcodes.length) {
        errors[`assignment_${i}_gl`] = 'Select at least one account';
      }
    });
  }

  if (!form.priority) errors.priority = 'Priority is required';

  const evidenceState = resolveEvidenceFieldState({
    evidence_required: form.evidence_required,
    no_of_evidence: form.noofevidence,
  });
  if (evidenceState.error) {
    errors.noofevidence = Array.isArray(evidenceState.error)
      ? evidenceState.error[0]
      : evidenceState.error;
  }

  const tags = form.tags || form.frequency || [];
  const tagList = Array.isArray(tags)
    ? tags.filter((t) => (typeof t === 'string' ? t.trim() : t?.name || t?.label))
    : String(tags || '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
  if (!tagList.length) errors.tags = 'Frequency is required';

  const assertions = form.assertion || form.assertions || [];
  const assertionList = Array.isArray(assertions)
    ? assertions.filter((item) => (typeof item === 'string' ? item.trim() : item?.name || item?.label))
    : String(assertions || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
  if (!assertionList.length) errors.assertion = 'Assertions is required';

  return errors;
}

export function validateCustomFieldForm(form = {}, existingColumns = []) {
  const errors = {};
  const displayName = (form.displayName || '').trim();

  if (!displayName) errors.displayName = 'Please enter display name';
  if (!form.dataType) errors.dataType = 'Please select data type';
  if (!form.cycleType) errors.cycleType = 'Please select cycle type';

  const duplicate = existingColumns.some(
    (c) =>
      !c.isSystem &&
      (c.displayName || c.displayText || '').toLowerCase() === displayName.toLowerCase() &&
      c._id !== form._id
  );
  if (duplicate) errors.displayName = 'Custom column name already exists';

  return errors;
}

export function filterTemplatesBySearch(templates = [], search = '') {
  const q = (search || '').trim().toLowerCase();
  if (!q) return templates;
  return templates.filter((t) => {
    const hay = [
      t.ref,
      t.reference,
      t.description,
      t.name,
      t.task_type,
      t.taskTypeDisplay,
      t.cycle_type,
      t.cycleType,
      t.category,
      t.subcategory,
      ...(Array.isArray(t.locationDisplay) ? t.locationDisplay : [t.locationDisplay]),
      t.frequencyDisplay,
      ...(Array.isArray(t.tags) ? t.tags : String(t.tags || '').split(',')),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
}
